import { afterNextRender, Directive, ElementRef, inject, OnDestroy } from '@angular/core';

/**
 * Startet ein <video> zuverlässig – auch bei clientseitiger (SPA-)Navigation –
 * UND performant: Quelle und Wiedergabe starten erst, wenn das Video im Viewport
 * sichtbar wird (IntersectionObserver), und pausieren wieder, sobald es ihn
 * verlässt. So wird below-the-fold nichts eager geladen oder dekodiert.
 *
 * Voraussetzung im Template: `preload="none"` (die Directive setzt es zusätzlich).
 * Der frühere SPA-Fix bleibt erhalten: Beim ersten Sichtbarwerden wird die – ggf.
 * per Binding gesetzte – Quelle einmal frisch geladen. Läuft nur im Browser.
 */
@Directive({
  selector: 'video[mbAutoplay]',
  standalone: true,
})
export class AutoplayVideoDirective implements OnDestroy {
  private readonly host = inject(ElementRef<HTMLVideoElement>);
  private observer?: IntersectionObserver;

  constructor() {
    afterNextRender(() => {
      const video = this.host.nativeElement;
      video.muted = true; // Autoplay-Policy
      video.preload = 'none'; // nichts vorab laden – erst bei Sichtbarkeit

      let loaded = false;
      const play = () => {
        if (!loaded) {
          // per Binding gesetzte <source> wird bei SPA-Navigation nicht immer
          // automatisch geladen – daher beim ersten Mal frisch laden.
          try {
            video.load();
          } catch {
            /* ignore */
          }
          loaded = true;
        }
        const p = video.play();
        if (p && typeof p.catch === 'function') p.catch(() => {});
      };

      if ('IntersectionObserver' in window) {
        this.observer = new IntersectionObserver(
          (entries) => {
            for (const entry of entries) {
              if (entry.isIntersecting) {
                play();
              } else if (!video.paused) {
                video.pause();
              }
            }
          },
          { threshold: 0.25 }
        );
        this.observer.observe(video);
      } else {
        play();
      }
    });
  }

  ngOnDestroy(): void {
    this.observer?.disconnect();
  }
}
